import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  AdminPageHeader,
  BulkActionModal,
  Theme,
  ButtonStyles,
  DropDown,
  MLPVersion,
  TableWithSearchTemplate,
} from '../../../ui/components';
import { LoggerService, dateTimeToCustomFormat,printPDF } from '../../../ui/utils';
import { DropDownData } from '../../../ui/components/Form/DropDown/types/propsType';
import { useAdminToast } from '../../../ui/components/AdminToast/AdminToast';
import { useAPICall, useBulkActionState } from '../../../hooks';
import { DynamicObjectInterface } from '../../../sdk/type';
import RedemptionService from '../../../sdk/services/redemptionService';
import OperationCenterErrorCard, {
  ErrorType,
} from '../../../ui/components/OperationCenterErrorCard/OperationCenterErrorCard';
import CampaignService from '../../../sdk/services/campaignService';
import {
  MasterData,
  dataSearchOption,
  CampaignOption,
  getRedemptionListConfig,
  bulkActionOption,
  bulkActionEditConfig,
} from './redemptionConfig';

import styles from './index.module.scss';

interface GroupedReward {
  rewardCode: string;
  rewardName: string;
  quantity: number;
}


export const AdminGeneralRedemptionList = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const apiCall = useAPICall();
  const [masterData, setMasterData] = useState<MasterData | null>();
  const [campaignList, setCampaignList] = useState<CampaignOption[]>([]);
  const [selectedCampaign, setSelectedCampaign] = useState<CampaignOption>();
  const [displayEmpty, setDisplayEmpty] = useState(false);
  const [selectedRow, setSelectedRow] = useState<DynamicObjectInterface>(null);
  const [redemptionList, setRedemptionList] = useState<DynamicObjectInterface>([]);
  const [resetKey, setResetKey] = useState<number>(0);
  const [cancelledList, setCancelledList] = useState([]);
  const [defaultSearchOptions, setDefaultSearchOptions] = useState<DynamicObjectInterface>(null);
  //const [drawerOpen, setDrawerOpen] = useState(false);
  const [showBulkActionEditModal, setShowBulkActionEditModal] = useState<Boolean>(false);
  const [checkedItemsCount, setCheckedItemsCount] = useState<number>(0);
  const [showDelete, setShowDelete] = useState(false);
  const [selectedData, setSelectedData] = useState<DynamicObjectInterface[]>([]);

  const { adminToast } = useAdminToast();
  const { selectedAction, resetTrigger, clearAll, handleActionInputChange } = useBulkActionState();

  useEffect(() => {
    fetchMasterData();
  }, []);

  useEffect(() => {
    if (masterData) {
      setCampaignList(
        masterData.campaign
          ?.map(
            c =>
              ({
                ...c,
                title: `${c.campaignId} - ${c.campaignName}`,
                value: c.campaignId ?? '',
              }) as CampaignOption,
          )
          .sort((a, b) => {
            if (a.campaignStart > b.campaignStart) return -1;
            if (a.campaignStart < b.campaignStart) return 1;
            return 0;
          }),
      );
    }
  }, [masterData]);

  useEffect(() => {
    if (campaignList?.length) {
      const params = new URLSearchParams(location.search);
      const campaignId = params.get('campaignId') ?? '';
      if (campaignId) {
        setSelectedCampaign(prev => {
          let _selectedCampaign = campaignList.find(c => c.campaignId === campaignId);
          if (_selectedCampaign) return { ..._selectedCampaign };
          else return prev;
        });
      } else {
        let latest: CampaignOption = campaignList.reduce((latest: DynamicObjectInterface, current) => {
          const currentStartDate = new Date(current.campaignStart);
          const latestStartDate = latest ? new Date(latest.campaignStart) : null;

          if (!latestStartDate || currentStartDate > latestStartDate) return { ...current, value: current.campaignId };
          else return latest;
        }, null);
        navigate(`?campaignId=${latest.value}`, { replace: true });
      }
    }
  }, [campaignList, location]);

  useEffect(() => {
    if (selectedCampaign) {
      fetchRedemptionListByCampaignId();
      let searchOptions = sessionStorage.getItem('FilterOptions');
      if (searchOptions) {
        const isoDatePattern = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/;
        const parsed = JSON.parse(searchOptions, (_key, value) => {
          if (typeof value === 'string' && isoDatePattern.test(value)) {
            return new Date(value);
          }
          return value;
        });
        setDefaultSearchOptions(parsed);
      } else {
        setDefaultSearchOptions(null);
      }
    }
  }, [selectedCampaign]);

  const fetchRedemptionListByCampaignId = async () => {
    let campaignId = selectedCampaign?.value || '';
    const result = await apiCall(RedemptionService.getRedemptionList, campaignId);
    if (result.status === 200) {
      setDisplayEmpty(result.data.data?.length === 0);
      setRedemptionList(result.data.data ?? []);
      setCancelledList(
        result.data.data
          ?.filter((i: DynamicObjectInterface) => 
            i.status === 'CANCELLED'
          )
          ?.map((d: DynamicObjectInterface) => (d.orderNumber)),
      );
    } else {
      setDisplayEmpty(true);
      LoggerService.log('Failed to retrieve redemption list by campaign');
    }
  };

  const fetchMasterData = async () => {
    let list = {
      campaign: {
        endpoint: CampaignService.getCampaigns,
      },
    };
    try {
      const results = await Promise.all(Object.values(list)?.map(l => apiCall(l.endpoint)));

      setMasterData(
        Object.keys(list).reduce((result: DynamicObjectInterface, key, idx) => {
          result[key] = results[idx]?.data?.data?.filter(
            (campaign: DynamicObjectInterface) => campaign.campaignType === 'PROMOTION'
          );
          return result;
        }, {}),
      );
    } catch (error) {
      LoggerService.log('Failed to retrieve participant list', error);
    }
  };

  const onEdit = (id: string) => {
    if (id !== null) {
       navigate(`/admin/promotion/manage-redemption/edit?=${id}&campaignId=${selectedCampaign?.value}`)
    }
  };

  const onPrint = async (id: string) => {

    const payload = {
      orderNumber: id,
      campaignId: selectedCampaign?.value
    }
    const result = await apiCall(RedemptionService.getRedemptionByOrder, payload);
    if (result.status === 200 && selectedCampaign) {
      const start = dateTimeToCustomFormat(selectedCampaign.campaignStart, 'DD MMM YYYY');
      const end = dateTimeToCustomFormat(selectedCampaign.campaignEnd, 'DD MMM YYYY');
      const headers = [t('admin.redemption.invoice.header') + selectedCampaign.campaignName, 
        t('admin.redemption.invoice.header2') + start + ' - ' + end]
      const lines = [t('admin.redemption.invoice.orderingABO') +  result?.data?.data?.orderAboId, 
        t('admin.redemption.invoice.volumeToABO') +  result?.data?.data?.volumeAboId, 
        t('admin.redemption.invoice.orderNumber') +  id, ]
      const columns = [t('admin.redemption.invoice.number'), t('admin.redemption.invoice.sku'), t('admin.redemption.invoice.reward'), t('admin.redemption.invoice.quantity')];
      const groupedRewards = result?.data?.data?.rewards.reduce((acc : GroupedReward[], r : { rewardCode: string; rewardName: string }) => {
      const existing = acc.find(item => item.rewardCode === r.rewardCode);
        if (existing) {
          existing.quantity += 1;
        } else {
          acc.push({
            rewardCode: r.rewardCode,
            rewardName: r.rewardName,
            quantity: 1,
          });
        }
        return acc;
      }, [] as { rewardCode: string; rewardName: string; quantity: number }[]);
      const rows = groupedRewards?.map((r: GroupedReward, i: number) => [
        i + 1,
        r.rewardCode,
        r.rewardName,
        r.quantity,
      ]);
      printPDF(
        headers,
        lines,
        columns,
        result?.data?.data?.status === 'CANCELLED',
        rows,
        { size: "half-A4-horizontal", orientation: "l" }
      );
    } else {
      adminToast({
        message: result?.data?.message,
        theme: Theme.ERROR,
      });
      LoggerService.log('Failed to retrieve redemption detail by orderNumber');
    }

  };

  const handleInputChange = (value: DynamicObjectInterface) => {
    sessionStorage.removeItem('Pagination');
    navigate(`?campaignId=${value.campaignId}`);
  };

  const handleReset = () => {
    setDefaultSearchOptions(null);
    setResetKey(prev => prev + 1);
    sessionStorage.removeItem('FilterOptions');
    sessionStorage.removeItem('Pagination');
  };

  const buttonConfig = [
    {
      title: t('admin.redemption.addRedemption'),
      buttonStyle: ButtonStyles.SOLID,
      onClick: () => {
        navigate(`/admin/promotion/manage-redemption/new?id=${selectedCampaign?.value}`);
      },
    },
  ];

  const onBulkEdit = () => {
    onModalOpenClose();
  };

  const onBulkDelete = () => {
    setShowDelete(!showDelete);
  };

  const onModalOpenClose = () => {
    setShowBulkActionEditModal(!showBulkActionEditModal);
  };

  const onConfirmBulkEdit = (data: DynamicObjectInterface) => {
    onModalOpenClose();
    bulkUpdateRedemption(data);
  };

  const bulkUpdateRedemption  = async (data: DynamicObjectInterface) => {

    const payload = {
      status: data.rewardStatus.toUpperCase(),
      orderNumbers: selectedData,
      campaignId: selectedCampaign?.value,
    };

    const result = await apiCall(RedemptionService.updateRedemptionByOrder, payload);
    if (result.status === 200 && result?.data?.status) {
      navigate(`/admin/promotion/manage-redemption/list?campaignId=${selectedCampaign?.value}`);
      adminToast({
        message: result?.data?.message,
        theme: Theme.SUCCESS,
      });
    } else {
      adminToast({
        message: result?.data?.message,
        theme: Theme.ERROR,
      });
    }
  };

  //   const onDeleteAction = async () => {
  //   try {
  //     const response = await apiCall(RedemptionService.deleteInventory, `${selectedRowId}`);

  //     if (response?.status === 200) {
  //       // Assuming the server responds with success status
  //       LoggerService.log('Inventory deleted successfully');
  //       adminToast({
  //         message: 'Inventory deleted successfully',
  //         theme: Theme.SUCCESS,
  //       });
  //       fetchRedemptionListByCampaignId();
  //     } else {
  //       LoggerService.log('Failed to delete redemption');
  //     }
  //   } catch (error) {
  //     LoggerService.log('Error deleting redemption', error);
  //   } finally {
  //     // Close the modal whether the deletion was successful or not
  //     setShowDelete(false);
  //   }
  // };

  //  const deletePrompt = (
  //   <>
  //     {showDelete && (
  //       <Modal
  //         primaryButtonLabel={''}
  //         headline={`${t('admin.redemption.deleteInventory')}`}
  //         closeIcon={false}
  //         containerWidth={'460px'}
  //         primaryButton={
  //           <Button
  //             title={t('admin.redemption.confirm')}
  //             type={ButtonTypes.BUTTON}
  //             onClick={() => {
  //               onDeleteAction();
  //             }}
  //             size={ButtonSize.SMALL}
  //             buttonStyle={ButtonStyles.SOLID}
  //             data-testid="test-add-button-popup"
  //           />
  //         }
  //         secondaryButton={
  //           <Button
  //             title={t('admin.redemption.cancel')}
  //             type={ButtonTypes.BUTTON}
  //             onClick={() => {
  //               setShowDelete(false);
  //             }}
  //             size={ButtonSize.SMALL}
  //             buttonStyle={ButtonStyles.OUTLINE}
  //             theme={Theme.BLACK}
  //             data-testid="test-cancel-button-popup-delete-modal"
  //           />
  //         }
  //       />
  //     )}
  //   </>
  // );

  const popup = (
    <div data-testid="test-pop-up">
      {showBulkActionEditModal && (
        <BulkActionModal
          labels={{
            modalTitle: 'Bulk Edit',
            confirm: 'Submit',
            cancel: 'Cancel',
          }}
          data={{}}
          onCancel={onModalOpenClose}
          onConfirm={onConfirmBulkEdit}
          onModalClose={onModalOpenClose}
          config={bulkActionEditConfig(t)}
          note={'Note: Limit each batch to 500 entries for optimal performance'}
        />
      )}
    </div>
  );

  // const drawerDetails = (
  //   <>
  //     {drawerOpen && (
  //       <Drawer
  //         onClose={() => setDrawerOpen(false)}
  //         closeOnBackdropClick={true}
  //         closeIcon={true}
  //         header={<div>Details</div>}
  //         drawerWidth={'490px'}
  //         bottomGapFooter="0px"
  //         footer={
  //           <div>
  //             <Button
  //               title="Edit"
  //               onClick={() => onEdit(selectedRow)}
  //               size={ButtonSize.SMALL}
  //               buttonWidth={ButtonWidth.FULL_WIDTH}
  //             />
  //           </div>
  //         }
  //       >
  //         <div className={styles['summary-pane-body']}>
  //           <div className={styles['basic-details']}>
  //             <hr className={styles['horizontal-divider']} />
  //             <div className={styles['title-value']}>
  //               <div className={styles['two-columns']}>
  //                 <Label title={t('Reward Status')} value={selectedRow?.status} />
  //               </div>
  //             </div>
  //           </div>
  //         </div>
  //       </Drawer>
  //     )}
  //   </>
  // );

  return (
    <div>
      <AdminPageHeader title={t('admin.redemption.redemptionList')} button={buttonConfig}/>
      <div className={styles['coach-list-top']}>
        <DropDown
          name="campaignId"
          dropdownLabel={`${t('admin.teams.campaign')}`}
          disabledFilter={false}
          mlpVersion={MLPVersion.ONE}
          value={selectedCampaign?.value}
          data={campaignList as unknown as DropDownData[]}
          configData={{
            label: 'title',
            onSelect: (selectedOption: DropDownData) => {
              handleInputChange(selectedOption);
            },
          }}
          readOnly={false}
        />
        <div className={styles['coach-list-top-layout']}>
          <div className={styles['coach-list-top-info']}>
            <div>{t('admin.generalCoach.period')}:</div>
            <p>
              {selectedCampaign?.campaignStart && selectedCampaign.campaignEnd
                ? `${dateTimeToCustomFormat(selectedCampaign.campaignStart, 'DD MMM YYYY')} - ${dateTimeToCustomFormat(
                    selectedCampaign.campaignEnd,
                    'DD MMM YYYY',
                  )}`
                : '-'}
            </p>
          </div>
          <div className={styles['coach-list-top-info']}>
            <div>{t('admin.generalCoach.status')}:</div>
            <p>{selectedCampaign?.status ?? '-'}</p>
          </div>
        </div>
      </div>
      {displayEmpty ? (
        <div className={styles['no-results-container']}>
          <OperationCenterErrorCard errorType={ErrorType.NO_RECORD} />
        </div>
      ) : (
        <TableWithSearchTemplate
          key={resetKey}
          dataSource={redemptionList}
          searchByStrip
          //hideAddButton
          getColumns={getRedemptionListConfig}
          data={dataSearchOption}
          defaultSearchOptions={defaultSearchOptions}
          handleReset={handleReset}
          rowKey="orderNumber"
          selected={`${selectedRow?.orderNumber}`}
          cb={{ onEdit, onPrint, onBulkEdit, onBulkDelete }}
          onRowClick={row => {
            setSelectedRow(row);
            //setDrawerOpen(true);
          }}
          enableCheckbox
          showBulkAction
          getBulkActions={bulkActionOption}
          setSelectedKeys={setSelectedData}
          selectedAction={selectedAction}
          resetTrigger={resetTrigger}
          clearAll={clearAll}
          handleActionInputChange={handleActionInputChange}
          checkedItemsCount={checkedItemsCount}
          setCheckedItemsCount={setCheckedItemsCount}
          disabledItems={cancelledList}
        />
      )}
      {/* {deletePrompt} */}
      {popup}
    </div>
  );
};
